import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Avatar, CircularProgress, IconButton, Tooltip } from "@mui/material";
import Edit from "assets/img/svg/edit";
import StyledButton from "components/shared/StyledButton";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { TinyBody } from "helpers/TypographyComponents";
import { getInitials } from "helpers/utils";
import { t } from "i18next";
import { MouseEvent, useMemo } from "react";
import type { Agreement, AgreementSigner } from "store/agreementsStore";
import {
    AgreementSignatureStatus,
    deleteAgreement,
    fetchAgreementSigners,
    fetchAgreements,
} from "store/agreementsStore";
import type { IUserData } from "store/settingsStore";
import { useAppDispatch } from "store/store";
import type { TFetchStatus } from "store/types";
import type { IUser } from "types";

import type { AgreementsValidatorsStatus, Validator } from "..";

/**
 * Format Agreement table action column (btn)
 */
interface IAgreementTableAction {
    agreement: Agreement;
    userData: IUserData | null;
    handleOpenAgreement: () => void;
    onOpenTracking?: (title: string, filename: string) => void;
    onToggleRefusal?: () => void;
    isRefusalOpen?: boolean;
}
export const AgreementTableAction = ({
    agreement,
    userData,
    handleOpenAgreement,
    onOpenTracking,
    onToggleRefusal,
    isRefusalOpen = false,
}: IAgreementTableAction) => {
    const { isRelatedResourceDeleted, status, signedStatus, validators, emittedBy, mySignatureUrl, mySignedStatus } =
        agreement;
    const dispatch = useAppDispatch();

    if (isRelatedResourceDeleted) {
        const handleDelete = async (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!window.confirm(t("ContractValidation.ConfirmDeleteOrphan"))) return;
            await dispatch(deleteAgreement({ agreementId: agreement.id }));
            dispatch(fetchAgreements());
        };
        return (
            <Tooltip title={t("ContractValidation.DeleteOrphanAgreement") as string}>
                <IconButton onClick={handleDelete} size="small" color="error">
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    }

    const inSignatureProcess = signedStatus !== AgreementSignatureStatus.TO_SIGN;
    const toReview = status === "negotiating" && validators.some((v) => v.userId === userData?.id);
    const isEditing = emittedBy === userData?.id && status === "draft";
    const isRefusedDocoon = agreement.signProvider === "docoon" && signedStatus === AgreementSignatureStatus.REFUSED;

    const isDocoon = agreement.signProvider === "docoon" && inSignatureProcess;

    const getMenuOptions = () => {
        const options: { id: string; name: string; icon: JSX.Element; action: () => void }[] = [];
        options.push({
            id: "display",
            name: t(
                agreement.type === "avenant"
                    ? "ContractValidation.DisplayAvenant"
                    : "ContractValidation.DisplayContract",
            ),
            icon: <VisibilityOutlinedIcon style={{ fontSize: 16 }} />,
            action: handleOpenAgreement,
        });
        if (mySignatureUrl && mySignedStatus === AgreementSignatureStatus.SENT) {
            options.push({
                id: "sign",
                name: t("ContractValidationEdit.SignContract"),
                icon: <DrawOutlinedIcon style={{ fontSize: 16 }} />,
                action: () => window.open(mySignatureUrl, "_blank"),
            });
        }
        if (toReview) {
            options.push({
                id: "review",
                name: t(
                    agreement.type === "avenant"
                        ? "ContractValidation.ReviewAvenant"
                        : "ContractValidation.ReviewContract",
                ),
                icon: <VisibilityOutlinedIcon style={{ fontSize: 16 }} />,
                action: handleOpenAgreement,
            });
        }
        if (isEditing) {
            options.push({
                id: "edit",
                name: t(agreement.type === "avenant" ? "ContractValidation.EditAvenant" : "ContractValidation.Edit"),
                icon: <Edit />,
                action: handleOpenAgreement,
            });
        }
        if (isDocoon) {
            options.push({
                id: "tracking",
                name: t("ContractValidation.TrackSignature"),
                icon: <HistoryOutlinedIcon style={{ fontSize: 16 }} />,
                action: () => onOpenTracking?.(agreement.relatedResourceName, agreement.filename),
            });
        }
        return options;
    };

    return (
        <div className="flex items-center justify-end gap-2">
            {isRefusedDocoon && (
                <Tooltip title={t("ContractValidation.ViewRefusalReasonTooltip") as string}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleRefusal?.();
                        }}
                        style={{ color: "var(--danger-text)" }}
                    >
                        <VisibilityOutlinedIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}
            <ThreeDotMenu options={getMenuOptions()} />
        </div>
    );
};

/**
 * Format Agreement table validators column
 */
interface IAgreementValidators {
    /**
     * Agreement validators
     */
    validators: Validator[];
    /**
     * Members users by ids
     */
    membersById: {
        [id: string]: IUser;
    };
}

export const AgreementTableValidators = ({ validators, membersById }: IAgreementValidators) => {
    return (
        <span className="permissions">
            {[...validators]
                .sort((a, b) => a?.order - b?.order)
                .map((validator: Validator, i: number) => {
                    if (!membersById[validator.userId]) return null;
                    const user = membersById[validator.userId];
                    const username = `${user?.firstname ?? ""} ${user?.lastname ?? ""}`.trim();
                    const limit = 6;
                    const rest: Validator[] = validators.slice(limit);
                    const statusCss: { [key in AgreementsValidatorsStatus]: React.CSSProperties } = {
                        idle: {},
                        waiting: { outlineColor: "var(--info)" },
                        approve: { outlineColor: "var(--success)" },
                        decline: { outlineColor: "var(--danger)" },
                    };
                    const restUsername = rest
                        .map((item) => membersById[item.userId])
                        .map((user) => `${user.firstname} ${user.lastname}`)
                        .join(", ");
                    return i < limit ? (
                        <Tooltip
                            key={username}
                            title={`${username} ${t(`ContractValidation.StatusIs${validator.status}`)}`}
                        >
                            <Avatar
                                key={username}
                                sx={{ width: "24px", height: "24px" }}
                                className="permission-element"
                                style={{ ["--i" as any]: i, ...statusCss[validator.status] }}
                                alt={username}
                            >
                                {getInitials(username)}
                            </Avatar>
                        </Tooltip>
                    ) : (
                        i == limit && (
                            <Tooltip key={username} title={restUsername}>
                                <Avatar
                                    key={username}
                                    sx={{ width: "24px", height: "24px" }}
                                    className="permission-element"
                                    style={{ ["--i" as any]: i }}
                                    alt={username}
                                >
                                    {rest.length == 1 ? getInitials(username) : `+${rest.length}`}
                                </Avatar>
                            </Tooltip>
                        )
                    );
                })}
        </span>
    );
};

/**
 * Format Agreement table signatories
 */
interface IAgreementTableSignatories {
    /**
     * Agreement data
     */
    agreement: Agreement;
    /**
     * Agreement Signers list
     */
    agreementSigners: AgreementSigner[];
    /**
     * Fetch agreement signers status
     */
    agreementSignersStatus: TFetchStatus;
    /**
     * Company user list
     */
    userList: IUser[];
}
export const AgreementTableSignatories = ({
    agreement,
    agreementSigners,
    agreementSignersStatus,
    userList,
}: IAgreementTableSignatories) => {
    const { signers: signersCount, beneficiary, id: agreementId } = agreement;
    // The internal signer is optional (never set with Docoon): count beneficiaries only
    const beneficiarySignersCount = (signersCount as number) - (agreement.publisherSignerId ? 1 : 0);
    const dispatch = useAppDispatch();
    const currentSignersDataAgreementId = agreementSigners?.[0]?.agreementId;
    const isLoadingSigners = agreementSignersStatus !== "succeeded";
    const publisherSigner = userList.find((user) => +user.id === agreement.publisherSignerId);
    const publisherSignerName = `${publisherSigner?.lastname ?? ""} ${publisherSigner?.firstname ?? ""}`.trim();

    const handleShowSigners = () => {
        // Only fetch signers if the current data signers is from an another agreement id
        if (currentSignersDataAgreementId !== agreementId) {
            dispatch(fetchAgreementSigners({ agreementId }));
        }
    };

    /**
     * Format Signers infos as a string for tooltip
     * show external signatories and intern signatory
     */
    const signersInfos = useMemo(() => {
        const beneficiaries: string[] = [];
        return agreementSigners.reduce(
            (signersAcc, signer, i) => {
                const isLastIndex = i === agreementSigners.length - 1;
                if (signer.relatedResource === "beneficiary") {
                    beneficiaries.push(signer.name);
                }
                if (signer.relatedResource === "user") {
                    signersAcc.publisher = signer.name;
                }
                if (isLastIndex) {
                    const beneficiarySigners = beneficiaries.join(", ");
                    signersAcc.beneficiaries = beneficiarySigners;
                }
                return signersAcc;
            },
            { beneficiaries: "", publisher: "" },
        );
    }, [agreementSigners]);

    const TooltipTitle = () => {
        const style = { color: "white" };
        return (
            <div className="agreement-table-signers-tooltip">
                {isLoadingSigners ? (
                    <CircularProgress className="progress-icon" />
                ) : (
                    <div className="signatories">
                        <div>
                            <label className="signatories-label">
                                {`${t("ContractValidationEdit.ExternalSignatoriesLabel")}`}
                            </label>
                            <TinyBody style={style}>{signersInfos.beneficiaries}</TinyBody>
                        </div>
                        <div>
                            <label className="signatories-label">{`${t(
                                "ContractValidationEdit.InternalSignatoryLabel",
                            )}`}</label>
                            <TinyBody style={style}>{signersInfos.publisher}</TinyBody>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {beneficiarySignersCount > 1 ? (
                <Tooltip title={<TooltipTitle />}>
                    <div
                        onMouseEnter={handleShowSigners}
                        style={{ width: "max-content" }}
                        className="multiple-signers-label"
                    >
                        <span>{`${t("ContractValidationEdit.SignersCount", {
                            signersCount: signersCount as number,
                            count: signersCount as number,
                        })}`}</span>
                        <GroupsOutlinedIcon sx={{ width: "14px" }} />
                    </div>
                </Tooltip>
            ) : (
                <div className="single-signer-label">
                    <Tooltip
                        title={
                            publisherSignerName &&
                            `${t("ContractValidationEdit.InternalSignatoryLabel")} ${publisherSignerName}`
                        }
                    >
                        <span>{beneficiary}</span>
                    </Tooltip>
                </div>
            )}
        </>
    );
};
